import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, HostBinding, input, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { ItemDataService } from '../../services/item-data.service';
import { ValueComponent } from '../value/value.component';
import { ValueHistoryComponent } from '../value-history/value-history.component';
import { GearData, JewelleryData, UniqueItem } from '../../models/item-data-models';
import { PricedItem } from '../../models/economy-models';
import { BaseComponent } from '../base.component';
import { EndgameContent, getEndgameContentIcon, Rarity } from '../../models/enums';
import { GameItem } from '../../models/game-item.models';
import { SimulatorResult } from '../../models/simulator-models';
import { NavigationService } from '../../services/navigation.service';
import { CtrlKeyService } from '../../services/ctrl-key.service';
import { FilterStoreService } from '../../services/filter-store.service';
import { EconomyService } from '../../services/economy.service';

@Component({
  selector: 'app-item-label',
  imports: [NgTemplateOutlet, ValueComponent, ValueHistoryComponent],
  templateUrl: './item-label.component.html',
  styleUrls: ['./item-label.component.scss', '../label-common.scss']
})
export class ItemLabelComponent extends BaseComponent implements OnChanges, OnInit {
  @HostBinding('attr.data-base-type')
  get hostNavBaseType(): string {
    return this.baseType;
  }

  @Input({ required: true }) baseType!: string;
  @Input() gameItem?: GameItem;
  @Input() image?: string;
  @Input() unique?: boolean;
  @Input() restricted?: string;
  @Input() restrictedContent?: EndgameContent;
  @Input() gem?: boolean;
  @Input() gear?: boolean;
  @Input() jewellery?: boolean;
  @Input() rarity?: Rarity | 'Hidden';
  @Input() hideTooltip?: boolean;
  @Input() displayLabel?: string;
  @Input() hiddenByQuickFilter?: boolean;
  @Input() devHighlight?: boolean;
  @Input() showPreviousValue?: boolean;
  @Input() manualTier?: boolean;
  @Input() favouriteCurrencyAdjusted?: boolean;
  @Input() tooltipOnly?: boolean;
  @Input() showItemLibraryRemoveHint?: boolean;
  @Input() showRandomLootAddHint?: boolean;
  @Input() showSimulatorRuleInfo?: boolean;
  @Input() showCopyItemTextHint?: boolean;
  @Input() simulatorResult?: SimulatorResult;
  enableGearTierNavigation = input(false);
  enableJewelleryTierNavigation = input(false);

  description?: string;
  gemDescription?: string;
  effects?: string[];

  itemType?: string;
  unidentifiedUniqueEntries: { name: string; image?: string; bossDrop?: string; bossDropContentIcon?: string }[] = [];
  text?: string;
  name?: string;
  implicitMods?: string[];
  explicitMods?: string[];
  flavorText?: string | null;
  itemBaseType?: string;
  droppedByBoss?: string;
  droppedByContent?: EndgameContent;
  droppedByContentIcon?: string;
  restrictedContentIcon?: string;
  dustValue?: number;
  dustValuePerSlot?: number;
  displayDustValue?: string;

  gearData?: GearData;
  gearIlvlValues: { ilvl: number; pricedItem: PricedItem }[] = [];
  jewelleryData?: JewelleryData;
  jewelleryIlvlValues: { ilvl: number; pricedItem: PricedItem }[] = [];
  ilvlValues: { ilvl: number; pricedItem: PricedItem }[] = [];
  equippableStaticData?: GearData | JewelleryData;
  tierNavigationEnabled = false;
  tierNavigationHint = '';

  showTooltip = false;
  isBaseTypeHovered = signal(false);
  baseTypeNavHovered = computed(() =>
    this.settings.enableRuleNavigation()
    && (this.enableGearTierNavigation() || this.enableJewelleryTierNavigation())
    && this.isBaseTypeHovered()
    && this.ctrlKeyService.isCtrlHeld()
  );

  constructor(
    public filterStoreService: FilterStoreService,
    private itemDataService: ItemDataService,
    private economyService: EconomyService,
    private navigationService: NavigationService,
    private ctrlKeyService: CtrlKeyService,
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['restrictedContent']) {
      this.updateRestrictedContentIcon();
    }
    if (this.tooltipOnly) {
      this.onMouseEnter();
    }
  }

  ngOnInit(): void {
    if (!this.image) {
      const flatItem = this.itemDataService.getFlatItemDataByBaseTypeName(this.baseType);
      if (flatItem?.baseType.image) {
        this.image = flatItem.baseType.image;
      }
    }
    if (this.unique) {
      this.droppedByBoss = this.itemDataService.getUniqueBossDrop(this.baseType);
      this.droppedByContent = this.itemDataService.getUniqueBossDropContent(this.baseType);
      this.droppedByContentIcon = this.droppedByContent
        ? getEndgameContentIcon(this.droppedByContent)
        : undefined;
    }
    this.updateRestrictedContentIcon();
  }

  private updateRestrictedContentIcon(): void {
    this.restrictedContentIcon = this.restrictedContent
      ? getEndgameContentIcon(this.restrictedContent)
      : undefined;
  }

  onMouseEnter() {
    if (this.gameItem) {
      this.resetData();
      if (this.gameItem.rarity === 'Unique' && this.gameItem.identified === false) {
        const uniques = this.itemDataService.getUniquesList()
          .filter(u => u.baseType === this.gameItem!.baseType);
        if (uniques.length === 1) {
          this.unique = true;
          this.baseType = uniques[0].name;
        } else if (uniques.length > 1) {
          this.itemType = 'UnidentifiedUnique';
          this.unidentifiedUniqueEntries = this.buildUnidentifiedUniqueEntries(uniques);
          this.baseType = this.gameItem.baseType;
          this.rarity = Rarity.Unique;
          this.image = undefined;
        } else {
          this.baseType = this.gameItem.baseType;
          this.rarity = Rarity.Unique;
          this.gem = this.gameItem.itemClass === 'Skill Gems' || this.gameItem.itemClass === 'Support Gems';
          this.jewellery = ['Amulets', 'Rings', 'Belts'].includes(this.gameItem.itemClass);
          this.gear = !this.jewellery && this.itemDataService.getAllGearTypes().includes(this.gameItem.itemClass);
          const baseType = this.itemDataService.getFlatItemDataByBaseTypeName(this.gameItem!.baseType);
          this.image = baseType?.baseType.image;
        }
      } else if (this.gameItem.rarity === 'Unique' && this.gameItem.identified !== false) {
        this.unique = true;
        this.baseType = this.gameItem.name!;
      } else {
        this.baseType = this.gameItem.baseType;
        this.rarity = this.gameItem.rarity === 'Currency' ? undefined : this.gameItem.rarity;
        this.gem = this.gameItem.itemClass === 'Skill Gems' || this.gameItem.itemClass === 'Support Gems';
        this.jewellery = ['Amulets', 'Rings', 'Belts'].includes(this.gameItem.itemClass);
        this.gear = !this.jewellery && this.itemDataService.getAllGearTypes().includes(this.gameItem.itemClass);
        const baseType = this.itemDataService.getFlatItemDataByBaseTypeName(this.gameItem!.baseType);
        this.image = baseType?.baseType.image;
      }
    }

    if (this.jewellery) {
      this.jewelleryData = this.itemDataService.getJewellery(this.baseType) ?? { name: this.baseType, implicits: [] };
      this.equippableStaticData = this.jewelleryData;
      this.implicitMods = this.jewelleryData.implicits.map(implicit => this.highlightValues(implicit));
      const jewelleryValues = this.itemDataService.getJewelleryValues(this.baseType);
      this.jewelleryIlvlValues = jewelleryValues
        ? Object.entries(jewelleryValues.ilvls)
            .filter(([, v]) => v.price != null)
            .map(([ilvl, v]) => ({
              ilvl: Number(ilvl),
              pricedItem: { name: this.baseType, price: v.price!, ts: '' },
            }))
            .sort((a, b) => a.ilvl - b.ilvl)
        : [];
      this.ilvlValues = this.jewelleryIlvlValues;
      this.tierNavigationEnabled = this.enableJewelleryTierNavigation() && !!this.jewellery;
      this.tierNavigationHint = 'Jewellery Tier List';
    } else if (this.gear) {
      this.gearData = this.itemDataService.getGear(this.baseType);
      this.equippableStaticData = this.gearData;
      this.implicitMods = this.gearData?.implicits.map(implicit => this.highlightValues(implicit));
      const gearValues = this.itemDataService.getGearValues(this.baseType);
      this.gearIlvlValues = gearValues
        ? Object.entries(gearValues.ilvls)
            .filter(([, v]) => v.price != null)
            .map(([ilvl, v]) => ({
              ilvl: Number(ilvl),
              pricedItem: { name: this.baseType, price: v.price!, ts: '' },
            }))
            .sort((a, b) => a.ilvl - b.ilvl)
        : [];
      this.ilvlValues = this.gearIlvlValues;
      this.tierNavigationEnabled = this.enableGearTierNavigation() && !!this.gear;
      this.tierNavigationHint = 'Gear Tier List';
    } else if (this.unique) {
      const unique = this.itemDataService.getUnique(this.baseType);
      if (unique) {
        this.itemType = 'Unique';
        this.text = unique.name + ' ' + unique.baseType;
        this.name = unique.name;
        this.image = unique.image ? '/img/uniques/' + unique.image : undefined;
        this.implicitMods = unique.implicitMods.map(mod => this.highlightValues(mod));
        this.explicitMods = unique.explicitMods.map(mod => this.highlightValues(mod));
        this.flavorText = unique.flavorText;
        this.itemBaseType = unique.baseType;
        this.droppedByBoss = this.itemDataService.getUniqueBossDrop(unique.name);
        this.droppedByContent = this.itemDataService.getUniqueBossDropContent(unique.name);
        this.droppedByContentIcon = this.droppedByContent
          ? getEndgameContentIcon(this.droppedByContent)
          : undefined;
        this.dustValue = unique.dustValue;
        this.dustValuePerSlot = unique.dustValuePerSlot;

        // Compute display dust value based on setting
        if (this.dustValue) {
          const showPerSlot = this.settings.showUniquesDustValuePerSlot();
          if (showPerSlot && this.dustValuePerSlot) {
            this.displayDustValue = `${this.dustValuePerSlot} / slot`;
          } else {
            this.displayDustValue = this.dustValue.toString();
          }
        }
      }
    } else if (this.gem) {
      const currency = this.itemDataService.getCurrency(this.baseType);
      if (currency?.gemDescription) {
        // Lineage and Kalguuran support gems are tiered as currency but copied as Support Gems
        this.gem = false;
        this.description = currency.description;
        this.gemDescription = currency.gemDescription;
        this.effects = currency.effects?.map(effect => this.highlightValues(effect));
      } else {
        const gem = this.itemDataService.getGem(this.baseType);
        this.description = gem?.description;
        this.effects = [gem?.tags?.join(', ') || ''];
      }
    } else {
      const description = this.itemDataService.getCurrency(this.baseType);
      this.description = description?.description;
      this.gemDescription = description?.gemDescription;
      this.effects = description?.effects?.map(effect => this.highlightValues(effect));
    }

    this.showTooltip = true;
  }

  resetData() {
    this.description = undefined;
    this.gemDescription = undefined;
    this.effects = undefined;
    this.itemType = undefined;
    this.unidentifiedUniqueEntries = [];
    this.text = undefined;
    this.name = undefined;
    this.implicitMods = undefined;
    this.explicitMods = undefined;
    this.flavorText = undefined;
    this.itemBaseType = undefined;
    this.droppedByBoss = undefined;
    this.droppedByContent = undefined;
    this.droppedByContentIcon = undefined;
    this.dustValue = undefined;
    this.dustValuePerSlot = undefined;
    this.displayDustValue = undefined;
    this.gearData = undefined;
    this.gearIlvlValues = [];
    this.jewelleryData = undefined;
    this.jewelleryIlvlValues = [];
    this.ilvlValues = [];
    this.equippableStaticData = undefined;
    this.tierNavigationEnabled = false;
    this.tierNavigationHint = '';
    this.unique = undefined;
    this.gem = undefined;
    this.gear = undefined;
    this.jewellery = undefined;
    this.rarity = undefined;
    this.image = undefined;
  }

  private buildUnidentifiedUniqueEntries(uniques: UniqueItem[]): { name: string; image?: string; bossDrop?: string; bossDropContentIcon?: string }[] {
    return uniques
      .map(u => {
        const bossDrop = this.itemDataService.getUniqueBossDrop(u.name);
        const bossDropContent = this.itemDataService.getUniqueBossDropContent(u.name);
        return {
          name: u.name,
          image: u.image ? '/img/uniques/' + u.image : undefined,
          price: this.economyService.getUniqueValue(u.name)?.price ?? 0,
          bossDrop,
          bossDropContentIcon: bossDropContent ? getEndgameContentIcon(bossDropContent) : undefined,
        };
      })
      .sort((a, b) => b.price - a.price)
      .map(({ name, image, bossDrop, bossDropContentIcon }) => ({ name, image, bossDrop, bossDropContentIcon }));
  }

  highlightValues(effect: string) {
    if (!this.settings.highlightStats()) {
      return effect;
    }
    // Avoid double processing if already highlighted
    if (effect.includes('<span class="it-effect-value">')) {
      return effect;
    }
    return effect.replace(/([\+\-]\(\d+(\.\d+)?-\d+(\.\d+)?\)|\(\+?-?\d+(\.\d+)?-\d+(\.\d+)?\)|\+?-?\d+(\.\d+)?)/g, '<span class="it-effect-value">$&</span>');
  }

  onMouseLeave() {
    this.showTooltip = false;
  }

  onBaseTypeMouseEnter() {
    this.isBaseTypeHovered.set(true);
  }

  onBaseTypeMouseLeave() {
    this.isBaseTypeHovered.set(false);
  }

  onBaseTypeClick(event: MouseEvent) {
    if (!this.settings.enableRuleNavigation()) return;
    if (!event.ctrlKey && !event.metaKey) return;

    const baseType = this.gameItem?.baseType ?? this.baseType;

    if (this.enableJewelleryTierNavigation() && this.jewellery) {
      event.preventDefault();
      event.stopPropagation();
      this.navigationService.navigateToJewelleryTier(baseType);
    } else if (this.enableGearTierNavigation() && this.gear) {
      event.preventDefault();
      event.stopPropagation();
      this.navigationService.navigateToGearTier(baseType);
    }
  }
}
